import React, { cloneElement, Component } from "react";
import cns from "classnames";
import { ArrowUpBoldCircle } from "mdi-material-ui";
import debug from "debug";
import locale from "@bonliva-crm/locale";
import { Classes, ThemeType } from "@theme";
import { WithTooltip } from "@components";
import { withStyles } from "@material-ui/styles";

const log = debug("app:components:StickyContainer");

export type StickyContainerProps = {
  classes: Classes<typeof styles>;
  getScrollContainer?: () => HTMLElement;
  children: React.ReactElement<any>;
  hidePullBtn?: boolean;
  offsetTop?: number; // offset the pull down menu from the top edge by.. (if there is something else at the top of the page)
};

type State = {};

class StickyContainer extends Component<StickyContainerProps, State> {
  static defaultProps: Partial<StickyContainerProps> = {
    getScrollContainer: () => document.body,
    offsetTop: 0
  };

  nodeOrig = React.createRef<HTMLDivElement>(); // what stays in place
  node = React.createRef<HTMLDivElement>(); // what gets pulled up and down
  nodeHeight = 0;
  nodeLeft = 0;
  nodeRight = 0;
  lastScrollTop = 0;

  scrollConteiner: HTMLElement;

  // isSticky = false;
  isHidden = true;
  isOpen = false;
  isPulledUp = false;

  constructor(props) {
    super(props);
  }

  componentDidMount() {
    this.init();
  }

  componentDidUpdate() {
    this.init();
  }

  componentWillUnmount() {
    const scrollContainer = this.getScrollContainer();

    if (!scrollContainer) {
      return;
    }

    scrollContainer.removeEventListener("scroll", this.onContainerScroll);
    scrollContainer.removeEventListener("resize", this.measureOrig);
  }

  render() {
    const { children, classes } = this.props;

    return (
      <div style={{ position: "relative" }}>
        <div className={classes.static} ref={this.nodeOrig} data-test="StickyContainer">
          {cloneElement(children)}
        </div>

        <div
          ref={this.node}
          className={cns("StickyContainer__pulldown", classes.pullDown)}
          style={{
            top: this.props.offsetTop
          }}
        >
          {cloneElement(children)}

          {!this.props.hidePullBtn && (
            <div className={classes.upBtn}>
              <WithTooltip title={locale.Hide}>
                <ArrowUpBoldCircle onClick={this.pullUp} />
              </WithTooltip>
            </div>
          )}
        </div>
      </div>
    );
  }

  init() {
    this.measureOrig();
    this.listen();
    this.animate();
  }

  measureOrig = () => {
    const rect = this.nodeOrig.current.getBoundingClientRect();
    this.nodeLeft = rect.left;
    this.nodeRight = document.body.clientWidth - rect.right;
  };

  listen = () => {
    log("listen", this.props);

    const scrollContainer = this.getScrollContainer();

    if (!scrollContainer) {
      return;
    }

    this.lastScrollTop = scrollContainer.scrollTop;
    scrollContainer.addEventListener("scroll", this.onContainerScroll);
    scrollContainer.addEventListener("resize", this.measureOrig);
  };

  doAnimate = () => {
    if (!this.node || !this.node.current) {
      return;
    }

    window.requestAnimationFrame(this.doAnimate);
    this.animate();
  };

  onContainerScroll = (e: UIEvent) => {
    log("onContainerScroll", e);

    if (!this.node) {
      return;
    }

    const scrollContainer = this.getScrollContainer();
    const currentScrollTop = scrollContainer.scrollTop;
    const yDiff = currentScrollTop - this.lastScrollTop;

    log("currentScrollTop", { currentScrollTop, yDiff });

    this.isHidden = currentScrollTop === 0;

    if (currentScrollTop === 0) {
      this.isOpen = false;
    } else if (currentScrollTop < this.nodeHeight) {
      // do nothing
    } else {
      this.isOpen = this.isOpen ? yDiff < 0 : yDiff < -25;
    }

    // reset isPulledUp when scrolling down
    if (yDiff > 0) {
      this.isPulledUp = false;
    }

    this.animate();
    this.lastScrollTop = currentScrollTop;
  };

  private pullUp = () => {
    this.isPulledUp = true;
    this.animate();
  };

  private animate = () => {
    log("animate", this.isOpen, this.isHidden);
    const { offsetTop } = this.props;

    this.nodeHeight = this.node.current.getBoundingClientRect().height;
    const y = this.isOpen && !this.isPulledUp ? this.nodeHeight : 0;
    this.node.current.style.top = `-${this.nodeHeight - offsetTop}px`;
    this.node.current.style.visibility = this.isHidden ? "hidden" : "visible";
    this.node.current.style.transform = `translateY(${y}px)`;
    this.node.current.style.left = `${this.nodeLeft}px`;
    this.node.current.style.right = `${this.nodeRight}px`;
  };

  private getScrollContainer() {
    const { getScrollContainer } = this.props;

    if (!this.scrollConteiner) {
      this.scrollConteiner = getScrollContainer();
    }

    return this.scrollConteiner;
  }
}

const styles = (theme: ThemeType) => ({
  static: { paddingBottom: 4 },

  pullDown: {
    position: "fixed",
    left: 72,
    right: 36,
    zIndex: 3,
    // transition: "transform 250ms cubic-bezier(0.645, 0.045, 0.355, 1)"
    transition: "transform 0.25s cubic-bezier(0.075, 0.82, 0.165, 1);"
  },

  upBtn: {
    position: "absolute",
    bottom: 0,
    right: 0,
    left: 0,
    textAlign: "center",
    padding: 8,
    cursor: "pointer",
    color: theme.palette.grey[200],
    marginLeft: "40%",
    marginRight: "40%",
    "&:hover": { color: theme.palette.grey[300] }
  }
});

export default withStyles(styles as any)(StickyContainer);
